import { Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import styled from "styled-components";
import { PALETTE, tooltipStyle } from "./constants";
import type { IStatVBarChart, IStatItem } from "./types";
import { ChartModeSelector } from "./ChartModeSelector";
import { useChartMode } from "./useChartMode";
import { ChartCardPanel } from "./ChartCardPanel";

const Panel = styled(ChartCardPanel)`
	.legend { display: flex; flex-wrap: wrap; gap: 4px 16px; margin: 0 0 4px; padding: 0; list-style: none; font-size: 12px; line-height: 18px; color: #667085; }
	.legend li { display: flex; align-items: center; gap: 6px; min-width: 0; overflow-wrap: anywhere; }
	.swatch { width: 16px; border-top: 2px solid; flex-shrink: 0; }
	.swatch.filled { width: 9px; height: 9px; border: 0; border-radius: 2px; }
	.swatch.total { border-top-width: 1px; }
	.empty { padding: 32px 0; color: #667085; font-size: 13px; }
`;

interface Props {
	item: IStatItem;
	preferenceKey: string;
	showTotal?: boolean;
	onPointClick?: (index: number) => void;
}

export const LineChartCard = ({ item, preferenceKey, showTotal = false, onPointClick }: Props) => {
	const { t, i18n } = useTranslation();
	const [chartMode, changeChartMode] = useChartMode(preferenceKey, "area");
	const rawData = item.data as IStatVBarChart[];
	const legendLabels = item.legend?.labels ?? item.labels ?? [];
	const colors = item.legend?.colors ?? item.colors ?? PALETTE;
	// The API defines series by position, not by a fixed set of status names.
	const seriesCount = rawData.reduce((count, point) => Math.max(count, Array.isArray(point.value) ? point.value.length : 1), legendLabels.length);
	const series = Array.from({ length: seriesCount }, (_, index) => ({
		key: `s${index}`,
		label: legendLabels[index] ?? t("stats.chart.series", { number: index + 1 }),
		color: colors[index % colors.length] ?? PALETTE[index % PALETTE.length],
	}));
	const chartData = rawData.map(point => {
		const values = Array.isArray(point.value) ? point.value : [point.value];
		const row: Record<string, string | number | null> = { label: point.label };
		let total: number | null = 0;
		series.forEach(({ key }, index) => {
			const value = values[index];
			row[key] = typeof value === "number" && Number.isFinite(value) ? value : null;
			total = total !== null && typeof row[key] === "number" ? total + row[key] : null;
		});
		row.total = total;
		return row;
	});
	const totalVisible = showTotal && chartMode === "area" && seriesCount > 1;
	const totalLabel = t("stats.labels.total");
	const format = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });

	return <Panel aria-label={item.title}>
		<div className="header">
			<h3><Activity size={20} color="#167ca6" aria-hidden="true" /><span>{item.title}</span></h3>
			<ChartModeSelector value={chartMode} onChange={changeChartMode} />
		</div>
		<div className="body">
			{rawData.length > 0 && seriesCount > 0 ? <>
				<ul className="legend">
					{totalVisible && <li><span className="swatch total" style={{ borderColor: "#167ca6" }} aria-hidden="true" />{totalLabel}</li>}
					{series.map(entry => <li key={entry.key}><span className={`swatch${chartMode === "line" ? "" : " filled"}`} style={{ borderColor: entry.color, backgroundColor: chartMode === "line" ? undefined : entry.color }} aria-hidden="true" />{entry.label}</li>)}
				</ul>
				<ResponsiveContainer width="100%" height={280} minWidth={0}>
					<ComposedChart key={chartMode} data={chartData} accessibilityLayer margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barGap={0} barCategoryGap="18%"
						style={{ cursor: onPointClick ? "pointer" : undefined }}
						onClick={state => {
							if (!state.isTooltipActive || state.activeTooltipIndex === undefined || state.activeTooltipIndex === null) return;
							const index = Number(state.activeTooltipIndex);
							if (Number.isInteger(index) && rawData[index]) onPointClick?.(index);
						}}>
						<CartesianGrid strokeDasharray="3 3" stroke="#edf0f4" vertical={false} />
						<XAxis dataKey="label" tick={{ fontSize: 11, fill: "#848799" }} height={20} tickSize={0} tickMargin={6} axisLine={false} tickLine={false} minTickGap={36} />
						<YAxis allowDecimals={false} domain={[0, "auto"]} width={56} tick={{ fontSize: 11, fill: "#848799" }} axisLine={false} tickLine={false} />
						<Tooltip contentStyle={tooltipStyle} formatter={value => format(Number(value))} />
						{series.map(entry => chartMode === "bar"
							? <Bar key={entry.key} dataKey={entry.key} name={entry.label} fill={entry.color} radius={[2, 2, 0, 0]} maxBarSize={12} isAnimationActive={false} />
							: chartMode === "area"
								? <Area key={entry.key} dataKey={entry.key} name={entry.label} stackId="series" type="linear" stroke="none" fill={entry.color} fillOpacity={0.3} dot={rawData.length === 1} activeDot={{ r: 4 }} isAnimationActive={false} />
								: <Line key={entry.key} dataKey={entry.key} name={entry.label} type="linear" stroke={entry.color} strokeWidth={1.5} dot={rawData.length === 1} activeDot={{ r: 4 }} isAnimationActive={false} />)}
						{totalVisible && <Line dataKey="total" name={totalLabel} type="linear" stroke="#167ca6" strokeWidth={1} dot={rawData.length === 1} activeDot={{ r: 4 }} isAnimationActive={false} />}
					</ComposedChart>
				</ResponsiveContainer>
			</> : <div className="empty" role="status">{t("stats.chart.empty")}</div>}
		</div>
	</Panel>;
};
